import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { callsApi } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import { toast } from 'sonner';
import { useTeamOptions } from '../hooks/useTeamOptions';

const typeIcon = { Outbound: 'PhoneOutgoing', Inbound: 'PhoneIncoming', Missed: 'PhoneMissed' };
const typeColor = {
  Outbound: 'text-blue-600 bg-blue-50',
  Inbound:  'text-emerald-600 bg-emerald-50',
  Missed:   'text-rose-600 bg-rose-50',
};
const outcomeBadge = {
  Connected: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Voicemail: 'bg-slate-50 text-slate-700 border-slate-200',
  Interested: 'bg-amber-50 text-amber-700 border-amber-200',
  'Not Interested': 'bg-rose-50 text-rose-700 border-rose-200',
  Callback: 'bg-violet-50 text-violet-700 border-violet-200',
};

// --- DIALOG COMPONENT: LOG A CALL ---
const LogCallDialog = ({ onCreated, disabled }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contact: '', phone: '', type: 'Outbound', duration: '00:00',
    outcome: 'Connected', agent: '', recording: false,
  });
  const team = useTeamOptions();
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.contact.trim() || !form.phone.trim()) { 
      toast.error('Contact name and phone are required'); 
      return; 
    }
    setSaving(true);
    try {
      const created = await callsApi.create({
        ...form,
        time: 'Just now',
        id: Date.now().toString()
      });
      onCreated(created);
      toast.success('Call entry successfully logged');
      setOpen(false);
      setForm({ ...form, contact: '', phone: '', duration: '00:00' });
    } catch (e) {
      toast.error('Failed to log call data');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <Icons.Plus className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">LOG CALL</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-wider font-bold">Log a Call</DialogTitle>
          <DialogDescription>Record and append specific details regarding standard customer interactions.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="font-bold uppercase text-xs">Contact Name *</Label><Input value={form.contact} onChange={e => update('contact', e.target.value)} /></div>
          <div><Label className="font-bold uppercase text-xs">Phone *</Label><Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 9XXXXXXXXX" /></div>
          <div>
            <Label className="font-bold uppercase text-xs">Call Type</Label>
            <Select value={form.type} onValueChange={v => update('type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['Outbound','Inbound','Missed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="font-bold uppercase text-xs">Duration (mm:ss)</Label><Input value={form.duration} onChange={e => update('duration', e.target.value)} placeholder="02:14" /></div>
          <div>
            <Label className="font-bold uppercase text-xs">Outcome</Label>
            <Select value={form.outcome} onValueChange={v => update('outcome', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Connected','Voicemail','Interested','Not Interested','Callback'].map(s =>
                  <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-bold uppercase text-xs">Agent Assigned</Label>
            <Select value={form.agent} onValueChange={v => update('agent', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {team.map(u =>
                  <SelectItem key={u.id} value={u.name}>{u.name} — {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>CANCEL</Button>
          <Button className="bg-brand-navy hover:bg-[#13294A] text-white font-bold" onClick={submit} disabled={saving}>
            {saving ? 'SAVING...' : 'LOG CALL'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- MAIN WRAPPER COMPONENT ---
const Calls = () => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const canCreate = hasPermission('calls', 'create');
  const canDelete = hasPermission('calls', 'delete');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');

  // Routing Filter States
  const [dashFilterActive, setDashFilterActive] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);

  // --- COMPONENT WORKFLOW STATES ---
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dialerActive, setDialerActive] = useState(false);
  const [currentLeadIndex, setCurrentLeadIndex] = useState(0);
  const [audioOpen, setAudioOpen] = useState(false);
  const [activePlayback, setActivePlayback] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Auto-Dialer Dummy Configurations
  const [dialerSettings, setDialerSettings] = useState({
    autoWrapUp: true,
    wrapUpTime: '30',
    recordingEnabled: true,
  });

  const mockLeadsQueue = [
    { name: "Rahul Sharma", phone: "+91 98765 43210" },
    { name: "Ananya Deshmukh", phone: "+91 95617 74112" },
    { name: "Amit Patil", phone: "+91 88888 23456" },
  ];

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try { 
        const res = await callsApi.list(); 
        if (isMounted) setData(res);
      } catch (e) { 
        toast.error("Failed to fetch server call logs");
      } finally { 
        if (isMounted) setLoading(false); 
      }
    })();
    return () => { isMounted = false; };
  }, []);

  // Sync dashboard route parameters
  useEffect(() => {
    if (location.state?.filterType === 'site_visit' && location.state?.filterDate === 'today') {
      setDashFilterActive(true);
      setTab('all');
    } else {
      setDashFilterActive(false);
    }
  }, [location.state]);

  // Execute Core Filtering Dynamically
  const list = useMemo(() => {
    let result = data;

    // 1. Core Tab Match
    result = result.filter(c => tab === 'all' || (c.type || '').toLowerCase() === tab.toLowerCase());

    // 2. Dashboard Router Filter (Site Visits Today)
    if (dashFilterActive) {
      // Try to find explicit matches for the word "visit"
      const exactMatches = result.filter(c => 
        [c.contact, c.outcome, c.time, c.agent].some(f => (f || '').toLowerCase().includes('visit'))
      );

      // Fallback: If no dummy data explicitly contains "visit", show highly engaged calls for today
      if (exactMatches.length === 0 && result.length > 0) {
        setUsedFallback(true);
        result = result.filter(c => ['Connected', 'Interested'].includes(c.outcome));
      } else {
        setUsedFallback(false);
        result = exactMatches;
      }
    } 
    // 3. Normal Search Bar
    else if (q) {
      result = result.filter(c => 
        [c.contact, c.phone, c.agent, c.outcome, c.time]
          .some(field => (field || '').toLowerCase().includes(q.toLowerCase()))
      );
    }

    return result;
  }, [data, tab, q, dashFilterActive]);

  const clearFilters = () => {
    setDashFilterActive(false);
    setUsedFallback(false);
    setQ('');
    navigate('.', { replace: true, state: null });
  };

  const connected = data.filter(c => (c.outcome || '').toLowerCase() === 'connected').length;
  const missed = data.filter(c => (c.type || '').toLowerCase() === 'missed').length;

  // --- BUTTON ACTIONS IMPLEMENTATIONS ---
  const handleInitiateDirectCall = (contactName, phoneNumber) => {
    toast.success(`Dialing out directly to ${contactName} (${phoneNumber})...`);
  };

  const handleOpenAudioModal = (callEntry) => {
    setActivePlayback(callEntry);
    setAudioOpen(true);
    setIsPlaying(true);
  };

  const togglePlayback = () => setIsPlaying(!isPlaying);

  return (
    <div className="space-y-5">
      
      {/* Dynamic Filter Banner */}
      {dashFilterActive && (
        <div className={`p-3 border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-medium animate-in fade-in zoom-in duration-300 ${usedFallback ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          <div className="flex items-center gap-2">
            <Icons.Filter className={`w-4 h-4 shrink-0 ${usedFallback ? 'text-amber-600' : 'text-blue-600'}`} />
            <span>
              {usedFallback ? (
                <>No explicit "Site Visit" calls found in dummy data. Falling back to show <strong>Connected & Interested</strong> calls.</>
              ) : (
                <>Showing <strong>Site Visit Related Calls</strong> for <strong>Today</strong>.</>
              )}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters} 
            className={`h-7 font-bold uppercase tracking-wider text-[10px] shrink-0 bg-white shadow-sm ${usedFallback ? 'text-amber-700 hover:bg-amber-100' : 'text-blue-700 hover:bg-blue-100'}`}
          >
            Clear Filters
          </Button>
        </div>
      )}

      {/* DIALER BANNER */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#0F1F38] to-[#1B3A5C] text-white p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-brand-gold flex items-center justify-center text-[#0F1F38]">
              <Icons.Phone className={dialerActive ? "w-6 h-6 animate-pulse text-amber-500" : "w-6 h-6"} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-gold">Auto-Dialer Engine</div>
              <div className="font-display text-xl font-bold">
                {dialerActive ? `Dialing Lead ${currentLeadIndex + 1}/${mockLeadsQueue.length}` : 'Predictive dialer ready — Leads queued'}
              </div>
              <div className="text-xs text-slate-300 mt-0.5">Avg connect rate: 64% • Active System Mode: WebRTC</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              className={`${dialerActive ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-brand-gold hover:bg-[#b89252] text-[#0F1F38]'} font-bold transition-all`} 
              onClick={() => setDialerActive(!dialerActive)}
            >
              {dialerActive ? (
                <>
                  <Icons.Square className="w-4 h-4 mr-1.5 fill-current" /> Stop Dialer
                </>
              ) : (
                <>
                  <Icons.Play className="w-4 h-4 mr-1.5 fill-current" /> Start Dialer
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              className="text-white border-white/40 bg-white/10 hover:bg-white/20 uppercase font-bold text-xs" 
              onClick={() => setSettingsOpen(true)}
            >
              <Icons.Settings className="w-4 h-4 mr-1.5" /> Settings
            </Button>
          </div>
        </div>
      </Card>

      {/* AUTO DIALER RUNTIME BAR */}
      {dialerActive && (
        <Card className="border-amber-200 bg-amber-50/50 p-4 animate-in slide-in-from-top duration-300">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <div className="text-xs text-slate-500 uppercase font-bold">Active Targets Queue</div>
              <div className="text-sm font-bold text-slate-800">
                Current: <span className="text-brand-navy">{mockLeadsQueue[currentLeadIndex].name}</span> ({mockLeadsQueue[currentLeadIndex].phone})
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button size="sm" variant="outline" className="bg-white border-slate-300" onClick={() => handleInitiateDirectCall(mockLeadsQueue[currentLeadIndex].name, mockLeadsQueue[currentLeadIndex].phone)}>
                <Icons.PhoneCall className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Dial Target
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="bg-white border text-slate-700" 
                disabled={currentLeadIndex >= mockLeadsQueue.length - 1} 
                onClick={() => setCurrentLeadIndex(prev => prev + 1)}
              >
                Skip Lead <Icons.ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* METRICS METADATA CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Calls Checked', value: data.length, icon: 'PhoneCall', color: 'text-brand-navy' },
          { label: 'Connected Calls', value: connected, icon: 'PhoneIncoming', color: 'text-emerald-600' },
          { label: 'Missed Attempts', value: missed, icon: 'PhoneMissed', color: 'text-rose-600' },
          { label: 'Avg Duration Time', value: '02:48', icon: 'Clock', color: 'text-amber-600' },
        ].map(s => {
          const Icon = Icons[s.icon] || Icons.Phone;
          return (
            <Card key={s.label} className="border-slate-200 card-lift">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide font-bold">{s.label}</div>
                  <div className="font-display text-2xl font-bold mt-1">{s.value}</div>
                </div>
                <Icon className={`w-7 h-7 ${s.color}`} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* SEARCH AND CONTROL TOOLBAR */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input 
              className="pl-9" 
              placeholder="Search parameters..." 
              value={q} 
              onChange={e => setQ(e.target.value)}
              disabled={dashFilterActive} 
            />
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="all" className="uppercase font-bold text-xs">All</TabsTrigger>
              <TabsTrigger value="outbound" className="uppercase font-bold text-xs">Outbound</TabsTrigger>
              <TabsTrigger value="inbound" className="uppercase font-bold text-xs">Inbound</TabsTrigger>
              <TabsTrigger value="missed" className="uppercase font-bold text-xs">Missed</TabsTrigger>
            </TabsList>
          </Tabs>
          <LogCallDialog onCreated={(c) => setData(d => [c, ...d])} disabled={!canCreate} />
        </CardContent>
      </Card>

      {/* CALL LOGS CORE TABLE */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Outcome</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}><td colSpan={7} className="p-2"><Skeleton className="h-10 w-full" /></td></tr>
              ))}
              
              {!loading && list.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-400">
                      <Icons.PhoneOff className="w-10 h-10 mb-2 opacity-50" />
                      <span className="italic font-medium">No matching call records discovered.</span>
                      {dashFilterActive && (
                        <Button variant="link" onClick={clearFilters} className="mt-2 text-brand-navy">
                          Show all calls instead
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {!loading && list.map(c => {
                const TI = Icons[typeIcon[c.type]] || Icons.Phone;
                const initials = (c.agent || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase();

                return (
                  <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${typeColor[c.type] || 'text-slate-600 bg-slate-50'}`}>
                        <TI className="w-4 h-4" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-800">{c.contact}</div>
                      <div className="text-[11px] font-mono text-slate-500">{c.phone}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-700">{c.duration}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`${outcomeBadge[c.outcome] || ''} uppercase font-bold text-[10px]`}>{c.outcome}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-7 h-7">
                          <AvatarFallback className="text-[10px] bg-brand-navy text-white font-bold">
                            {initials || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-slate-700 font-medium">{c.agent}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{c.time || 'Just now'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {c.recording && (
                          <Button size="icon" variant="ghost" className="text-brand-navy hover:text-blue-600" onClick={() => handleOpenAudioModal(c)} title="Play Call Audio">
                            <Icons.PlayCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="text-emerald-600 hover:text-emerald-700" onClick={() => handleInitiateDirectCall(c.contact, c.phone)} title="Trigger Click-to-Call">
                          <Icons.PhoneCall className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <DeleteButton
                            title="Remove log entry permanently?"
                            description="Are you certain you want to erase this logged data entry?"
                            onConfirm={async () => {
                              try {
                                await callsApi.remove(c.id);
                                setData(d => d.filter(x => x.id !== c.id));
                                toast.success('Log trace successfully purged');
                              } catch {
                                toast.error('Execution error removing data record');
                              }
                            }}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* COMPONENT MODAL LAYOUT: CONFIGURATION SETTINGS */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-wider text-md text-slate-800">Dialer Configurations</DialogTitle>
            <DialogDescription>Modify preferences adjusting client trunk connections and local parameters.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <Label className="font-bold text-xs uppercase">Auto-Advance Wrap-up</Label>
              <Switch checked={dialerSettings.autoWrapUp} onCheckedChange={(v) => setDialerSettings({...dialerSettings, autoWrapUp: v})} />
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold text-xs uppercase">Hold Intermission (Seconds)</Label>
              <Input type="number" value={dialerSettings.wrapUpTime} onChange={(e) => setDialerSettings({...dialerSettings, wrapUpTime: e.target.value})} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="font-bold text-xs uppercase">Force Audio Track Recording</Label>
              <Switch checked={dialerSettings.recordingEnabled} onCheckedChange={(v) => setDialerSettings({...dialerSettings, recordingEnabled: v})} />
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full bg-brand-navy text-white font-bold" onClick={() => { setSettingsOpen(false); toast.success('Settings metrics updated successfully'); }}>SAVE CHANGES</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COMPONENT MODAL LAYOUT: AUDIO PLAYBACK STREAM PLAYER */}
      <Dialog open={audioOpen} onOpenChange={setAudioOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold tracking-wider text-sm text-slate-800">Call Track Playback</DialogTitle>
            <DialogDescription>Reviewing audio interaction for lead: <span className="font-bold text-slate-900">{activePlayback?.contact}</span></DialogDescription>
          </DialogHeader>
          <div className="bg-slate-900 p-4 rounded-lg my-3 flex flex-col items-center justify-center space-y-3 text-white">
            <div className="flex items-center gap-1.5 h-8">
              {[0.3, 0.8, 0.4, 0.9, 0.2, 0.7, 0.5, 0.9, 0.3, 0.6].map((h, i) => (
                <div 
                  key={i} 
                  style={{ height: isPlaying ? `${h * 100}%` : '15%' }} 
                  className={`w-1.5 bg-brand-gold rounded-full transition-all duration-300 ${isPlaying ? 'animate-pulse' : ''}`}
                />
              ))}
            </div>
            <div className="text-[11px] font-mono text-slate-400">TRACK STREAM ID: {activePlayback?.id}</div>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button size="icon" className="rounded-full bg-brand-navy hover:bg-[#13294A] text-white w-12 h-12" onClick={togglePlayback}>
              {isPlaying ? <Icons.Pause className="w-5 h-5 fill-current" /> : <Icons.Play className="w-5 h-5 fill-current ml-0.5" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Calls;